import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// CustomFilter.filter_type ordinal for kanban_task. The store action takes the
// name, but SaveCustomView posts the integer.
export const KANBAN_TASK_FILTER_TYPE = 3;

export const DEFAULT_KANBAN_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const SINGLE_SELECT_INPUTS = [
  'searchSelect',
  'asyncSearchSelect',
  'booleanSelect',
];

/**
 * Reduces a selected option to the only field anything downstream reads. The
 * provider attaches icon VNodes to step, label and inbox options, and those
 * would otherwise be walked by snakecaseKeys and cloned by
 * filterQueryGenerator -- wasted work today, and a crash the day an icon is a
 * component VNode, whose refs JSON.stringify cannot serialize.
 */
const toBareId = value =>
  value && typeof value === 'object' && 'id' in value
    ? { id: value.id }
    : value;

/**
 * Turns the filter UI state into the API payload.
 *
 * Only the keys the backend reads are sent: the UI conditions also carry
 * display labels, which have no business being persisted in a saved view.
 *
 * @param {Array} filters - the camelCase UI conditions
 * @returns {Array} snake_case conditions ready to POST
 */
export const buildFilterPayload = filters => {
  if (!filters?.length) return [];

  const conditions = filters.map(
    ({ attributeKey, filterOperator, values, queryOperator }) => ({
      attributeKey,
      filterOperator,
      queryOperator,
      values: Array.isArray(values) ? values.map(toBareId) : toBareId(values),
    })
  );

  // No clone here: every level above is freshly built, and
  // filterQueryGenerator copies again before it mutates.
  return filterQueryGenerator(useSnakeCase(conditions)).payload;
};

/**
 * Compares two payloads by content rather than by serialization. One that made
 * a round trip through the API comes back with its keys in a different order
 * and without the undefined query_operator, so comparing the JSON directly
 * reports two identical filters as different.
 *
 * @param {Array} a - snake_case conditions
 * @param {Array} b - snake_case conditions
 * @returns {boolean}
 */
export const samePayload = (a, b) => {
  const canonical = payload =>
    JSON.stringify(
      (payload ?? []).map(condition => [
        condition.attribute_key,
        condition.filter_operator,
        condition.query_operator ?? null,
        condition.values,
      ])
    );

  return canonical(a) === canonical(b);
};

/**
 * Rebuilds the option object a select input expects from a bare stored value.
 * Falls back to the raw value as the label when the option list can't resolve
 * it -- which happens for asyncSearchSelect (contacts), whose options are only
 * fetched on demand.
 */
const toOption = (value, options) => {
  const match = options?.find(option => String(option.id) === String(value));
  return match ?? { id: value, name: String(value) };
};

const hydrateValues = (values, filterType) => {
  const list = Array.isArray(values) ? values : [values].filter(v => v != null);

  if (filterType?.inputType === 'multiSelect') {
    return list.map(value => toOption(value, filterType.options));
  }

  if (SINGLE_SELECT_INPUTS.includes(filterType?.inputType)) {
    return list.length ? toOption(list[0], filterType.options) : {};
  }

  // An attribute whose definition has not arrived yet (a task custom attribute
  // on first paint, typically). Keep every value instead of collapsing to the
  // first, or re-saving the view right then would silently drop the rest.
  if (!filterType && list.length > 1) {
    return list.map(value => toOption(value, undefined));
  }

  return list.length ? String(list[0]) : '';
};

/**
 * Turns a stored payload (saved view or persisted preference) back into the
 * shape the filter UI binds to.
 *
 * Resolves option labels from the live filterTypes rather than from a
 * per-attribute switch, so a new attribute needs no change here.
 *
 * @param {Array} payload - snake_case conditions as stored
 * @param {Array} filterTypes - from useKanbanTaskFilterContext
 * @returns {Array} camelCase UI conditions
 */
export const hydrateFilterPayload = (payload, filterTypes) => {
  if (!Array.isArray(payload) || !payload.length) return [];

  return payload.map(condition => {
    const filterType = filterTypes?.find(
      type => type.attributeKey === condition.attribute_key
    );

    return {
      attributeKey: condition.attribute_key,
      filterOperator: condition.filter_operator,
      // Always a concrete operator, including on the last condition: it becomes
      // the connector as soon as another row is appended, and an undefined here
      // renders the and/or select blank. buildFilterPayload strips the trailing
      // one on the way out.
      queryOperator: condition.query_operator ?? 'and',
      values: hydrateValues(condition.values, filterType),
      // Display-only, stripped again by buildFilterPayload. Lets the active
      // filter chips render in the user's language.
      attributeName: filterType?.attributeName,
      operatorLabel: filterType?.filterOperators?.find(
        operator => operator.value === condition.filter_operator
      )?.label,
    };
  });
};

/**
 * A funnel's steps are specific to it, so a view saved on one funnel is
 * meaningless on another. The board id travels inside the stored query.
 */
export const buildViewQuery = (filters, boardId) => ({
  payload: buildFilterPayload(filters),
  board_id: boardId,
});

export const viewsForBoard = (views, boardId) =>
  (views ?? []).filter(view => view.query?.board_id === boardId);
